import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import type {
  Entity,
  Rotation,
} from "@/shared/state/visualization/schema/v1/three-d";

import {
  FileSelector,
  PathSelector,
} from "../../../controls/DataSourceSelector";
import { isViewableAsPlot } from "../../../Panel/predicates";
import { useDataFiles, useFileIdToTopics } from "../../../WorkspaceCtx/hooks";
import { ConfigItem } from "../ConfigItem";

import { AxisPresetPicker } from "./AxisPresetPicker";
import styles from "./EntityConfig.module.css";
import {
  channelsOf,
  channelText,
  defaultRotationConvention,
  ROTATION_PRESET_OPTIONS,
  ROTATION_KIND_LABELS,
  ROTATION_KINDS,
  rotationConstantFor,
  rotationOfKind,
  withAxisComponent,
  withChannel,
  withChannelScale,
  withConvention,
  type RotationKind,
} from "./rotationSpec";

interface RotationConfigProps {
  entity: Entity;
  panelId: string;
}

/**
 * How an entity is oriented relative to its parent.
 *
 * The rotational counterpart of `PositionConfig`, and deliberately the same
 * shape: one file scoping the paths, then a channel per axis, each a constant or
 * a bound topic. What differs is that a rotation has three ways to say the same
 * thing, so a type control comes first and the channels follow from it. Euler
 * angles and quaternions are read in a coordinate convention, picked next, so
 * a pose topic's orientation binds without sign flips; a new rotation starts in
 * the convention of the entity's position.
 *
 * Every decision that is not JSX lives in `rotationSpec.ts` and is tested there.
 */
export function RotationConfig({ entity, panelId }: RotationConfigProps) {
  const dispatch = useVizDispatch();
  const availableFiles = useDataFiles();
  const fileIdToTopics = useFileIdToTopics();

  const rotation = entity.rotation;
  const kind: RotationKind = rotation?.type ?? "none";
  const channels = rotation ? channelsOf(rotation) : [];

  // The file a channel is already bound to wins, then the user's last pick, then
  // the only file there is — `FileSelector` hides itself when there is one, and
  // the paths would otherwise have nothing to offer. The same precedence
  // `PositionConfig` uses, and for the same reason: a rotation records its file
  // nowhere except in the bindings themselves.
  const boundFileId = channels
    .map((channel) => channel.value)
    .find((value) => typeof value !== "number")?.topic
    .association.association_id;
  const [pickedFile, setPickedFile] = React.useState<string | undefined>(
    undefined,
  );
  const effectiveFileId =
    boundFileId ??
    pickedFile ??
    (availableFiles.length === 1 ? availableFiles[0].file_id : undefined);
  const topicTree = effectiveFileId
    ? (fileIdToTopics[effectiveFileId] ?? [])
    : [];

  const commit = React.useCallback(
    (next: Rotation | undefined) => {
      dispatch(updateThreeDEntity(panelId, entity.id, { rotation: next }));
    },
    [dispatch, entity.id, panelId],
  );

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        Rotation
      </RobotoTypography>
      <div className={styles.container}>
        <FormControl size="small" fullWidth className={styles.field}>
          <InputLabel id="rotation-type-label" className={styles.input}>
            Rotation Type
          </InputLabel>
          <Select
            label="Rotation Type"
            labelId="rotation-type-label"
            value={kind}
            onChange={(e) =>
              commit(
                rotationOfKind(
                  e.target.value as RotationKind,
                  rotation,
                  defaultRotationConvention(entity.position),
                ),
              )
            }
            className={styles.input}
            inputProps={{ className: styles.input }}
          >
            {ROTATION_KINDS.map((option) => (
              <MenuItem key={option} value={option}>
                {ROTATION_KIND_LABELS[option]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {rotation && rotation.type !== "axis_angle" && (
          <AxisPresetPicker
            className={styles.displayControl}
            options={ROTATION_PRESET_OPTIONS}
            // Absent is how every rotation saved before conventions reads: in
            // the scene's own axes.
            value={ROTATION_PRESET_OPTIONS.find(
              (option) => option.id === (rotation.convention ?? "scene"),
            )}
            onChange={(option) => commit(withConvention(rotation, option.id))}
          />
        )}

        {rotation?.type === "axis_angle" && (
          <ConfigItem label="Axis">
            <div className={styles.fieldRow}>
              {(["X", "Y", "Z"] as const).map((name, index) => (
                <TextField
                  key={name}
                  label={`Axis ${name}`}
                  type="number"
                  size="small"
                  margin="dense"
                  fullWidth
                  autoComplete="off"
                  value={rotation.axis[index]}
                  onChange={(e) => {
                    const parsed = Number.parseFloat(e.target.value);
                    if (Number.isFinite(parsed)) {
                      commit(
                        withAxisComponent(rotation, index as 0 | 1 | 2, parsed),
                      );
                    }
                  }}
                  inputProps={{ step: 0.1, className: styles.input }}
                  InputLabelProps={{ className: styles.input }}
                  InputProps={{ className: styles.input }}
                  className={styles.input}
                />
              ))}
            </div>
          </ConfigItem>
        )}

        {rotation && (
          <>
            {availableFiles.length >= 2 && (
              <ConfigItem label="File">
                <FileSelector
                  className={styles.blockSpacing}
                  files={availableFiles}
                  selection={effectiveFileId}
                  onSelect={setPickedFile}
                />
              </ConfigItem>
            )}
            {/* Remounted when the file changes: a PathSelector reads its input
                value once, on mount, so a new topic tree reaches it no other
                way. The same trick both other bound forms here use. */}
            <div key={effectiveFileId ?? "none"}>
              {channels.map((channel) => (
                <ConfigItem key={channel.key} label={channel.label}>
                  <PathSelector
                    className={styles.blockSpacing}
                    initialInputValue={channelText(channel.value)}
                    isValidFreeText={(text) =>
                      rotationConstantFor(text) !== undefined
                    }
                    onChange={(node) =>
                      commit(
                        withChannel(rotation, channel.key, node.toTopicData()),
                      )
                    }
                    // Cleared, a channel is back at zero: no turn about its
                    // axis, which is where every channel starts.
                    onClear={() =>
                      commit(withChannel(rotation, channel.key, 0))
                    }
                    // A number that names no path is a constant, as it is for
                    // a joint: the way to hold an axis at a fixed angle.
                    onFreeText={(text) => {
                      const constant = rotationConstantFor(text);
                      if (constant !== undefined) {
                        commit(withChannel(rotation, channel.key, constant));
                      }
                    }}
                    options={topicTree}
                    optionFilter={isViewableAsPlot}
                  />
                </ConfigItem>
              ))}
            </div>
            {channels.some((channel) => channel.scale !== undefined) && (
              <div className={styles.blockSpacing}>
                <ConfigItem label="Scaling">
                  <div className={styles.fieldRow}>
                    {channels
                      .filter((channel) => channel.scale !== undefined)
                      .map((channel) => (
                        <TextField
                          key={channel.key}
                          label={`${channel.label} Scale`}
                          type="number"
                          size="small"
                          margin="dense"
                          autoComplete="off"
                          value={channel.scale}
                          // Converts a channel's units into radians — a topic
                          // publishing degrees wants pi/180 here.
                          onChange={(e) => {
                            const parsed = Number.parseFloat(e.target.value);
                            if (Number.isFinite(parsed)) {
                              commit(
                                withChannelScale(rotation, channel.key, parsed),
                              );
                            }
                          }}
                          inputProps={{ step: 0.1, className: styles.input }}
                          InputLabelProps={{ className: styles.input }}
                          InputProps={{ className: styles.input }}
                          className={styles.scaleField}
                        />
                      ))}
                  </div>
                </ConfigItem>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
